import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import { fetchAgencies, type Agency } from "../../../../state/agencies";
import { supabase } from "../../../../lib/supabase";
import type { SearchParams, SortParams } from "../../../Manager/types";
import { useCallback } from "react";
import { Box } from "@radix-ui/themes";

const SEARCH_COLUMNS = ["name"];

export function ManageAgencies() {
  const { t } = useTranslation();

  const fetchData = useCallback(
    (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) => fetchAgencies(limit, offset, searchParams, sortParams),
    [],
  );

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: Agency | null,
  ) => {
    const name = data.name as string;

    if (item) {
      const { error } = await supabase
        .from("agencies")
        .update({ name })
        .eq("id", item.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supabase.from("agencies").insert([{ name }]);
      if (error) throw new Error(error.message);
    }
  };

  const handleDelete = async (item: Agency) => {
    // Check if agency has territories
    const { count: territoriesCount } = await supabase
      .from("territories")
      .select("*", { count: "exact", head: true })
      .eq("agency_id", item.id);

    if (territoriesCount && territoriesCount > 0) {
      throw new Error(t("agencies.deleteErrorTerritories"));
    }

    // Check if agency has suppliers
    const { count: suppliersCount } = await supabase
      .from("suppliers")
      .select("*", { count: "exact", head: true })
      .eq("agency_id", item.id);

    if (suppliersCount && suppliersCount > 0) {
      throw new Error(t("agencies.deleteErrorSuppliers"));
    }

    const { error } = await supabase
      .from("agencies")
      .delete()
      .eq("id", item.id);
    if (error) throw new Error(error.message);
  };

  const validateForm = (data: Record<string, unknown>): boolean => {
    const name = data.name as string;
    if (!name?.trim()) return false;
    return true;
  };

  return (
    <Box p="4">
      <Manager.Root<Agency> title={t("agencies.title")} fetchData={fetchData}>
        <Manager.Sort
          columns={[
            { key: "name", isDefault: true, defaultOrder: "asc" },
            { key: "territory_count" },
          ]}
        />
        <Manager.Search>
          <Manager.TextSearch
            columns={SEARCH_COLUMNS}
            placeholder={t("agencies.searchPlaceholder")}
          />
        </Manager.Search>

        <Manager.CreateButton label={t("agencies.addAgency")} />

        <Manager.Table>
          <Manager.Column<Agency>
            header={t("agencies.name")}
            accessor={(a) => a.name}
            sortKey="name"
          />
          <Manager.Column<Agency>
            header={t("agencies.territoryCount")}
            accessor={(a) => a.territory_count ?? 0}
            sortKey="territory_count"
          />
          <Manager.Actions>
            <Manager.EditAction />
            <Manager.DeleteAction />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<Agency>
          title={(isEdit) =>
            isEdit ? t("agencies.editAgency") : t("agencies.addAgency")
          }
          description={(isEdit) =>
            isEdit
              ? t("agencies.editDescription")
              : t("agencies.createDescription")
          }
          onSubmit={handleSubmit}
          validate={validateForm}
        >
          <Manager.TextField
            name="name"
            label={t("agencies.name")}
            required
            placeholder={t("agencies.namePlaceholder")}
            getValue={(item) => (item as Agency).name}
          />
        </Manager.FormDialog>

        <Manager.DeleteDialog<Agency>
          getItemName={(a) => a.name}
          onDelete={handleDelete}
        />
      </Manager.Root>
    </Box>
  );
}
